import { formatBytes } from '@/services/dbConsole'
import { useExportApprovers } from './useExportApprovers'

/**
 * Who must approve this export. Required: the named person is told the request
 * is waiting (notification bell, and email where it is switched on), and only
 * they can decide it.
 */
export function ApproverPicker({ value, onChange }: { value: string; onChange: (email: string) => void }) {
  const approvers = useExportApprovers()
  const people = approvers.data?.approvers ?? []
  return (
    <label className="mb-2 block">
      <span className="mb-1 block text-[9px] uppercase tracking-[0.14em] text-stone-600">
        Second approver (told when this is raised)
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label="Second approver"
        className="w-full rounded border border-amber-300/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 focus:border-amber-300/30 focus:outline-none"
      >
        <option value="">Choose who approves…</option>
        {people.map((person) => (
          <option key={person.email} value={person.email}>
            {person.name} ({person.email})
          </option>
        ))}
      </select>
      {approvers.isSuccess && people.length === 0 && (
        <span role="alert" className="mt-1 block text-[10px] text-amber-200">
          No one else holds approval authority, so an export cannot be approved. Give another admin
          Database Console approval in Settings first.
        </span>
      )}
    </label>
  )
}

/** The attachment option and the plain statement of what an export carries. */
export function AttachmentsOption({
  checked,
  onChange,
  limitBytes,
  notice,
  label = 'Include the attachment files (all deals)',
}: {
  checked: boolean
  onChange: (next: boolean) => void
  limitBytes?: number
  notice?: string
  label?: string
}) {
  return (
    <div className="mb-2 rounded border border-amber-300/[0.07] px-2 py-1.5">
      <label className="flex cursor-pointer items-center gap-1.5 text-[10px] text-stone-200">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="h-3 w-3 accent-amber-400"
        />
        {label}
        {limitBytes ? <span className="text-stone-500">· up to {formatBytes(limitBytes)}</span> : null}
      </label>
      <p data-testid="export-attachments-notice" className="mt-1 text-[9px] leading-relaxed text-stone-500">
        {checked
          ? 'The files travel in the archive, one folder per deal, with an index and checksums. Any file left out is listed with its reason.'
          : 'Database records only: the files themselves are not in this export.'}{' '}
        {notice ??
          'To take files: one deal at a time (Documents, Download all), or everything at once through an Azure copy of the documents storage.'}
      </p>
    </div>
  )
}
